import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  TIME_CONTROLS,
  type FriendSummary,
  type ProfileDashboard,
  type Side,
  type ViewerRole,
} from "../../shared/game.ts";
import {
  acceptGameInvitation,
  ApiRequestError,
  createGame,
  createProfile,
  declineGameInvitation,
  getProfileDashboard,
  respondToFriendRequest,
  sendFriendRequest,
  updateProfile,
} from "../lib/api.ts";
import { displayFriendCode, friendCodeFromInvite } from "../lib/friend-code.ts";
import { relativeDate, sideLabel } from "../lib/format.ts";
import { isStandaloneApp } from "../lib/install.ts";
import {
  clearProfileToken,
  getPlayerName,
  getProfileToken,
  getRecentGames,
  recordRecentGame,
  setGameToken,
  setPlayerName,
  setProfileToken,
  type RecentGame,
} from "../lib/storage.ts";
import { useProfilePushNotifications } from "../hooks/useProfilePushNotifications.ts";
import { Icon } from "./Icon.tsx";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type HomeDialog = "create" | "add-friend" | "profile" | null;

const sideOptions: Array<{ value: Side | "random"; label: string; symbol: string }> = [
  { value: "white", label: "Белые", symbol: "♟︎" },
  { value: "random", label: "Случайно", symbol: "?" },
  { value: "black", label: "Чёрные", symbol: "♟︎" },
];

function roleText(role: ViewerRole): string {
  return role === "spectator" ? "Зритель" : sideLabel(role);
}

function cleanPlayerName(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function friendInitial(friend: { name: string }): string {
  return friend.name.trim().charAt(0).toLocaleUpperCase("ru-RU") || "♟";
}

function timeControlLabel(initialSeconds: number, incrementSeconds: number): string {
  return TIME_CONTROLS.find(
    (control) => control.initialSeconds === initialSeconds && control.incrementSeconds === incrementSeconds,
  )?.label ?? "Своя партия";
}

function NameField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <>
      <label className="field-label" htmlFor="player-name">Как вас зовут</label>
      <div className="input-wrap">
        <Icon name="user" />
        <input
          id="player-name"
          value={value}
          onChange={(event) => onChange(event.target.value.slice(0, 24))}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            event.currentTarget.blur();
          }}
          placeholder="Ваше имя"
          autoComplete="name"
          enterKeyHint="done"
          autoFocus
        />
      </div>
      <p className="name-once-note">Имя увидят только ваши друзья и соперники.</p>
    </>
  );
}

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const [visualViewport, setVisualViewport] = useState<{ height: number; offsetTop: number } | null>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const update = () => setVisualViewport({ height: viewport.height, offsetTop: viewport.offsetTop });
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, []);

  const viewportStyle = visualViewport
    ? ({ top: `${visualViewport.offsetTop}px`, bottom: "auto", height: `${visualViewport.height}px` } satisfies CSSProperties)
    : undefined;

  return (
    <div
      className="modal-backdrop setup-backdrop"
      role="presentation"
      style={viewportStyle}
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="modal-card setup-modal" role="dialog" aria-modal="true" aria-labelledby="home-dialog-title">
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Закрыть"><Icon name="x" /></button>
        <h2 id="home-dialog-title">{title}</h2>
        {children}
      </section>
    </div>
  );
}

export function HomePage({ initialFriendCode = null }: { initialFriendCode?: string | null }) {
  const [savedName, setSavedName] = useState(getPlayerName);
  const [name, setName] = useState(getPlayerName);
  const [profileToken, setLocalProfileToken] = useState<string | null>(getProfileToken);
  const [dashboard, setDashboard] = useState<ProfileDashboard | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<HomeDialog>(null);
  const [dialogAfterProfile, setDialogAfterProfile] = useState<Exclude<HomeDialog, "profile" | null> | null>(null);
  const [side, setSide] = useState<Side | "random">("random");
  const [clockEnabled, setClockEnabled] = useState(false);
  const [timeIndex, setTimeIndex] = useState(2);
  const [selectedFriendId, setSelectedFriendId] = useState("");
  const [friendCode, setFriendCode] = useState(() => initialFriendCode ? displayFriendCode(initialFriendCode) : "");
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [recent] = useState<RecentGame[]>(getRecentGames);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const initialCodeOpened = useRef(false);
  const push = useProfilePushNotifications(profileToken && dashboard ? profileToken : null);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      setProfileLoading(true);
      setProfileError(null);
      let token = getProfileToken();
      if (token) {
        try {
          const next = await getProfileDashboard(token);
          if (!cancelled) {
            setDashboard(next);
            setSavedName(next.profile.name);
            setName(next.profile.name);
          }
          return;
        } catch (caught) {
          if (!(caught instanceof ApiRequestError) || caught.status !== 401) {
            if (!cancelled) setProfileError(caught instanceof Error ? caught.message : "Не удалось загрузить профиль.");
            return;
          }
          clearProfileToken();
          token = null;
          if (!cancelled) setLocalProfileToken(null);
        } finally {
          if (token && !cancelled) setProfileLoading(false);
        }
      }

      const rememberedName = getPlayerName();
      if (!rememberedName) {
        if (!cancelled) {
          setProfileLoading(false);
          setDialog("profile");
        }
        return;
      }
      try {
        const session = await createProfile(rememberedName);
        if (!cancelled) {
          setProfileToken(session.token);
          setLocalProfileToken(session.token);
          setDashboard(session.dashboard);
          setSavedName(session.dashboard.profile.name);
          setName(session.dashboard.profile.name);
        }
      } catch (caught) {
        if (!cancelled) setProfileError(caught instanceof Error ? caught.message : "Не удалось создать профиль.");
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!profileToken || !dashboard) return;
    let stopped = false;
    const refresh = () => {
      void getProfileDashboard(profileToken)
        .then((next) => {
          if (!stopped) setDashboard(next);
        })
        .catch(() => undefined);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const interval = window.setInterval(refresh, 8_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [profileToken, Boolean(dashboard)]);

  useEffect(() => {
    if (!initialFriendCode || initialCodeOpened.current || profileLoading || !dashboard) return;
    initialCodeOpened.current = true;
    setFriendCode(displayFriendCode(initialFriendCode));
    setDialog("add-friend");
  }, [dashboard, initialFriendCode, profileLoading]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3_500);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    const onInstallPrompt = (event: Event) => {
      event.preventDefault();
      if (!isStandaloneApp()) setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", onInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const openDialog = (next: Exclude<HomeDialog, null>) => {
    setError(null);
    setSuccess(null);
    if (next !== "profile" && !dashboard) {
      setDialogAfterProfile(next);
      setDialog("profile");
      return;
    }
    if (next === "profile") setName(dashboard?.profile.name ?? savedName);
    if (next === "create") setSelectedFriendId(dashboard?.friends[0]?.id ?? "");
    setDialog(next);
  };

  const closeDialog = () => {
    setError(null);
    setSuccess(null);
    setDialogAfterProfile(null);
    setName(dashboard?.profile.name ?? savedName);
    setDialog(null);
  };

  const submitProfile = async (event: FormEvent) => {
    event.preventDefault();
    const clean = cleanPlayerName(name);
    if (!clean) {
      setError("Введите имя.");
      return;
    }
    setWorking("profile");
    setError(null);
    try {
      let next: ProfileDashboard;
      if (profileToken && dashboard) {
        next = await updateProfile(profileToken, clean);
      } else {
        const session = await createProfile(clean);
        setProfileToken(session.token);
        setLocalProfileToken(session.token);
        next = session.dashboard;
      }
      setPlayerName(next.profile.name);
      setSavedName(next.profile.name);
      setName(next.profile.name);
      setDashboard(next);
      const nextDialog = dialogAfterProfile;
      setDialogAfterProfile(null);
      if (nextDialog === "create") setSelectedFriendId(next.friends[0]?.id ?? "");
      setDialog(nextDialog);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось сохранить профиль.");
    } finally {
      setWorking(null);
    }
  };

  const addFriend = async (event: FormEvent) => {
    event.preventDefault();
    if (!profileToken) return;
    const code = friendCodeFromInvite(friendCode, window.location.origin);
    if (!code) {
      setError("Введите код из 8 символов, например ABCD-EFGH.");
      return;
    }
    setWorking("add-friend");
    setError(null);
    setSuccess(null);
    try {
      const next = await sendFriendRequest(profileToken, code);
      setDashboard(next);
      const recipient = next.outgoingFriendRequests.find(
        (request) => request.profile.friendCode.replace("-", "") === code,
      );
      setSuccess(recipient ? `Заявка для ${recipient.profile.name} отправлена.` : "Заявка уже отправлена.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось отправить заявку.");
    } finally {
      setWorking(null);
    }
  };

  const answerFriendRequest = async (requestId: string, accept: boolean) => {
    if (!profileToken || working) return;
    setWorking(`friend:${requestId}`);
    setError(null);
    try {
      setDashboard(await respondToFriendRequest(profileToken, requestId, accept));
      setToast(accept ? "Друг добавлен" : "Заявка отклонена");
    } catch (caught) {
      setToast(caught instanceof Error ? caught.message : "Не удалось обработать заявку.");
    } finally {
      setWorking(null);
    }
  };

  const startGame = async () => {
    if (!profileToken || !dashboard || !selectedFriendId || working) return;
    const friend = dashboard.friends.find((item) => item.id === selectedFriendId);
    if (!friend) {
      setError("Выберите друга.");
      return;
    }
    const control = clockEnabled ? (TIME_CONTROLS[timeIndex] ?? TIME_CONTROLS[2]) : TIME_CONTROLS[0];
    setWorking("create-game");
    setError(null);
    try {
      const game = await createGame({
        friendId: friend.id,
        preferredSide: side,
        initialSeconds: control.initialSeconds,
        incrementSeconds: control.incrementSeconds,
      }, profileToken);
      if (game.token) setGameToken(game.state.id, game.token);
      recordRecentGame(game.state.id, game.role, friend.name);
      window.location.assign(`/game/${game.state.id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось создать партию.");
      setWorking(null);
    }
  };

  const answerGameInvitation = async (invitationId: string, accept: boolean) => {
    if (!profileToken || !dashboard || working) return;
    const invitation = dashboard.gameInvitations.find((item) => item.id === invitationId);
    if (!invitation) return;
    setWorking(`game:${invitationId}`);
    try {
      if (accept) {
        const game = await acceptGameInvitation(profileToken, invitationId);
        if (game.token) setGameToken(game.state.id, game.token);
        recordRecentGame(game.state.id, game.role, invitation.from.name);
        window.location.assign(`/game/${game.state.id}`);
        return;
      }
      setDashboard(await declineGameInvitation(profileToken, invitationId));
      setToast("Приглашение отклонено");
    } catch (caught) {
      setToast(caught instanceof Error ? caught.message : "Не удалось обработать приглашение.");
    } finally {
      setWorking(null);
    }
  };

  const copyFriendCode = async () => {
    if (!dashboard) return;
    try {
      await navigator.clipboard.writeText(dashboard.profile.friendCode);
      setToast("Код друга скопирован");
    } catch {
      setToast("Не удалось скопировать код");
    }
  };

  const shareFriendInvite = async () => {
    if (!dashboard) return;
    const code = dashboard.profile.friendCode;
    const url = `${window.location.origin}/friend/${code}`;
    const text = `Добавь меня в друзья в приложении «Шахматы». Мой код: ${code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Добавить в друзья", text, url });
        return;
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setToast("Приглашение скопировано");
    } catch {
      setToast("Не удалось скопировать приглашение");
    }
  };

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const selectedFriend: FriendSummary | undefined = dashboard?.friends.find((friend) => friend.id === selectedFriendId);

  return (
    <main className="home-page home-simple social-home">
      <header className="home-header compact-home-header page-width">
        <a className="brand" href="/" aria-label="Шахматы, главная">
          <span className="brand-mark" aria-hidden="true">♞</span>
          <span>Шахматы</span>
        </a>
        <div className="home-header-actions">
          {installPrompt && (
            <button className="button button-small button-quiet" type="button" onClick={() => void install()}>
              <Icon name="install" /> Установить
            </button>
          )}
          {(dashboard?.profile.name || savedName) && (
            <button className="profile-button" type="button" onClick={() => openDialog("profile")} aria-label="Изменить имя">
              <Icon name="user" /><span>{dashboard?.profile.name || savedName}</span>
            </button>
          )}
        </div>
      </header>

      {profileLoading ? (
        <section className="home-loading page-width"><span className="spinner spinner-dark" /><p>Загружаем друзей…</p></section>
      ) : profileError && !dashboard ? (
        <section className="home-inline-error page-width">
          <h1>Не удалось открыть профиль</h1>
          <p>{profileError}</p>
          <button className="button button-primary" type="button" onClick={() => window.location.reload()}>Повторить</button>
        </section>
      ) : dashboard ? (
        <div className="social-home-content page-width">
          {push.status !== "subscribed" && (
            <section className={`home-notification-card notification-${push.status}`}>
              <span className="home-notification-icon"><Icon name="bell" /></span>
              <div>
                <strong>{push.status === "denied" ? "Уведомления выключены" : "Не пропускайте приглашения"}</strong>
                <p>
                  {push.status === "denied"
                    ? "Разрешите их в «Настройки → Уведомления → Шахматы», затем вернитесь сюда."
                    : "iPhone сообщит о заявке в друзья, приглашении и ходе соперника."}
                </p>
                {push.error && <small>{push.error}</small>}
              </div>
              <button className="button button-primary button-small" type="button" onClick={() => void push.enable()} disabled={push.status === "loading"}>
                {push.status === "loading" ? "Проверяем…" : push.status === "denied" ? "Проверить" : "Включить"}
              </button>
            </section>
          )}

          {dashboard.gameInvitations.length > 0 && (
            <section className="home-section incoming-section" aria-labelledby="game-invites-title">
              <div className="social-section-heading"><h2 id="game-invites-title">Приглашения в партию</h2><span>{dashboard.gameInvitations.length}</span></div>
              <div className="social-card-list">
                {dashboard.gameInvitations.map((invitation) => (
                  <article className="request-card game-request-card" key={invitation.id}>
                    <span className="friend-avatar">{friendInitial(invitation.from)}</span>
                    <div><strong>{invitation.from.name}</strong><small>{timeControlLabel(invitation.timeControl.initialSeconds, invitation.timeControl.incrementSeconds)} · вы играете {sideLabel(invitation.yourSide).toLocaleLowerCase("ru-RU")}</small></div>
                    <div className="request-actions">
                      <button className="button button-primary button-small" type="button" onClick={() => void answerGameInvitation(invitation.id, true)} disabled={Boolean(working)}>Играть</button>
                      <button className="button button-quiet button-small" type="button" onClick={() => void answerGameInvitation(invitation.id, false)} disabled={Boolean(working)}>Отклонить</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {dashboard.incomingFriendRequests.length > 0 && (
            <section className="home-section incoming-section" aria-labelledby="friend-requests-title">
              <div className="social-section-heading"><h2 id="friend-requests-title">Хотят добавить вас</h2><span>{dashboard.incomingFriendRequests.length}</span></div>
              <div className="social-card-list">
                {dashboard.incomingFriendRequests.map((request) => (
                  <article className="request-card" key={request.id}>
                    <span className="friend-avatar">{friendInitial(request.profile)}</span>
                    <div><strong>{request.profile.name}</strong><small>Заявка в друзья</small></div>
                    <div className="request-actions">
                      <button className="button button-primary button-small" type="button" onClick={() => void answerFriendRequest(request.id, true)} disabled={Boolean(working)}>Добавить</button>
                      <button className="button button-quiet button-small" type="button" onClick={() => void answerFriendRequest(request.id, false)} disabled={Boolean(working)}>Нет</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          <section className="social-play-card">
            <div className="social-play-piece" aria-hidden="true">♞</div>
            <div><span className="eyebrow">Партия один на один</span><h1>Сыграть с другом</h1><p>Выберите друга — приглашение появится у него в приложении.</p></div>
            <button className="button button-primary button-large" type="button" onClick={() => openDialog(dashboard.friends.length ? "create" : "add-friend")}>
              <Icon name={dashboard.friends.length ? "plus" : "user"} />
              {dashboard.friends.length ? "Новая партия" : "Пригласить друга"}
            </button>
          </section>

          <section className="home-section friends-section" aria-labelledby="friends-title">
            <div className="social-section-heading">
              <h2 id="friends-title">Друзья</h2>
              <button className="button button-small button-quiet" type="button" onClick={() => openDialog("add-friend")}><Icon name="plus" /> Добавить</button>
            </div>

            <div className="own-code-card">
              <div><span>Ваш код друга</span><strong>{dashboard.profile.friendCode}</strong></div>
              <button className="icon-button" type="button" onClick={() => void copyFriendCode()} aria-label="Скопировать код"><Icon name="copy" /></button>
              <button className="button button-primary button-small" type="button" onClick={() => void shareFriendInvite()}><Icon name="share" /> Пригласить</button>
            </div>

            {dashboard.friends.length > 0 ? (
              <div className="friend-list">
                {dashboard.friends.map((friend) => (
                  <button className="friend-row" type="button" key={friend.id} onClick={() => { setError(null); setSelectedFriendId(friend.id); setDialog("create"); }}>
                    <span className="friend-avatar">{friendInitial(friend)}</span>
                    <span><strong>{friend.name}</strong><small>В друзьях</small></span>
                    <Icon name="chevron" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="friends-empty"><p>Добавьте друга по коду — после этого вы сможете приглашать друг друга в партии.</p></div>
            )}

            {dashboard.outgoingFriendRequests.length > 0 && (
              <div className="outgoing-requests">
                <span>Ожидают ответа</span>
                {dashboard.outgoingFriendRequests.map((request) => <b key={request.id}>{request.profile.name}</b>)}
              </div>
            )}
          </section>

          {recent.length > 0 && (
            <section className="recent-section simple-recent home-section">
              <div className="social-section-heading"><h2>Недавние партии</h2></div>
              <div className="recent-list">
                {recent.map((game) => (
                  <a key={game.id} href={`/game/${game.id}`} className="recent-game">
                    <span className="recent-piece" aria-hidden="true">♟</span>
                    <span><strong>{game.opponentName ? `Партия с ${game.opponentName}` : "Открыть партию"}</strong><small>{roleText(game.role)} · {relativeDate(game.updatedAt)}</small></span>
                    <Icon name="chevron" />
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <section className="social-play-card page-width profile-empty-card">
          <div className="social-play-piece" aria-hidden="true">♞</div>
          <div><h1>Создайте профиль</h1><p>Он нужен, чтобы добавлять друзей и получать приглашения.</p></div>
          <button className="button button-primary button-large" type="button" onClick={() => openDialog("profile")}>Продолжить</button>
        </section>
      )}

      {dialog === "create" && dashboard && (
        <DialogShell title="Новая партия" onClose={closeDialog}>
          <form className="setup-form" onSubmit={(event) => event.preventDefault()}>
            <fieldset className="form-group">
              <legend className="field-label">С кем играем</legend>
              <div className="friend-picker">
                {dashboard.friends.map((friend) => (
                  <button key={friend.id} type="button" className={selectedFriendId === friend.id ? "selected" : ""} onClick={() => setSelectedFriendId(friend.id)}>
                    <span className="friend-avatar">{friendInitial(friend)}</span><span><strong>{friend.name}</strong><small>{selectedFriendId === friend.id ? "Выбран" : "Выбрать"}</small></span><Icon name={selectedFriendId === friend.id ? "check" : "chevron"} />
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="form-group">
              <legend className="field-label">Выберите цвет</legend>
              <div className="side-picker">
                {sideOptions.map((option) => (
                  <button key={option.value} type="button" className={side === option.value ? "selected" : ""} onClick={() => setSide(option.value)} aria-pressed={side === option.value}>
                    <span className={`side-symbol side-symbol-${option.value}`}>{option.symbol}</span>{option.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="clock-toggle">
              <span><Icon name="clock" /><span><b>Игра с часами</b><small>{clockEnabled ? "Выберите контроль времени" : "Без ограничения времени"}</small></span></span>
              <input type="checkbox" checked={clockEnabled} onChange={(event) => setClockEnabled(event.target.checked)} /><i aria-hidden="true" />
            </label>

            {clockEnabled && (
              <div className="time-picker compact-time-picker" aria-label="Контроль времени">
                {TIME_CONTROLS.slice(1).map((control, offset) => {
                  const index = offset + 1;
                  return <button key={control.label} type="button" className={timeIndex === index ? "selected" : ""} onClick={() => setTimeIndex(index)} aria-pressed={timeIndex === index}><strong>{control.label}</strong><small>{control.hint}</small></button>;
                })}
              </div>
            )}

            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="button button-primary button-large button-full" type="button" onClick={() => void startGame()} disabled={!selectedFriend || working === "create-game"}>
              {working === "create-game" ? <span className="spinner" /> : <Icon name="plus" />}
              {working === "create-game" ? "Отправляем…" : `Пригласить ${selectedFriend?.name ?? "друга"}`}
            </button>
          </form>
        </DialogShell>
      )}

      {dialog === "add-friend" && dashboard && (
        <DialogShell title="Добавить друга" onClose={closeDialog}>
          <form className="setup-form" onSubmit={addFriend}>
            <p className="dialog-lead">Введите код, который прислал друг. Можно вставить целое сообщение или ссылку.</p>
            <label className="field-label" htmlFor="friend-code">Код друга</label>
            <div className="input-wrap code-input-wrap">
              <Icon name="user" />
              <input id="friend-code" value={friendCode} onChange={(event) => setFriendCode(event.target.value.toUpperCase().slice(0, 200))} placeholder="ABCD-EFGH" autoCapitalize="characters" autoCorrect="off" spellCheck={false} enterKeyHint="send" autoFocus />
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            {success && <div className="form-success" role="status"><Icon name="check" />{success}</div>}
            <button className="button button-primary button-large button-full" type="submit" disabled={working === "add-friend"}>
              {working === "add-friend" ? <span className="spinner" /> : <Icon name="plus" />}{working === "add-friend" ? "Отправляем…" : "Отправить заявку"}
            </button>
            <div className="share-own-code"><span>Или отправьте свой код</span><strong>{dashboard.profile.friendCode}</strong><button className="button button-quiet button-full" type="button" onClick={() => void shareFriendInvite()}><Icon name="share" /> Поделиться приглашением</button></div>
          </form>
        </DialogShell>
      )}

      {dialog === "profile" && (
        <DialogShell title={dashboard ? "Ваш профиль" : "Давайте познакомимся"} onClose={closeDialog}>
          <form className="setup-form" onSubmit={submitProfile}>
            <NameField value={name} onChange={setName} />
            {dashboard && <div className="profile-code-note"><span>Код друга</span><strong>{dashboard.profile.friendCode}</strong></div>}
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="button button-primary button-large button-full" type="submit" disabled={working === "profile"}>{working === "profile" ? "Сохраняем…" : "Сохранить"}</button>
          </form>
        </DialogShell>
      )}

      {toast && <div className="toast" role="status"><Icon name="check" />{toast}</div>}
    </main>
  );
}
